import React, { useState, useEffect } from 'react';
import { ChatMessage } from '../../data/initialContactsData';
import { getMessages, addMessage } from '../../utils/portalStorage';

const CHANNELS = [
  { id: 'general-announcements', name: '📢 General Royal Announcements', desc: 'Official proclamations & public festival notices' },
  { id: 'planning-committee', name: '🏛️ Planning & Steering Committee', desc: 'Central festival executive committee updates' },
  { id: 'diaspora-coordination', name: '🌍 Diaspora Coordination', desc: 'UK, US, European & global diaspora delegates forum' },
  { id: 'cultural-performances', name: '🎭 Cultural Troupes & Dances', desc: 'Rehearsals, masques, and traditional performances' },
  { id: 'sponsorship-fundraising', name: '💎 Sponsorship & Fundraising', desc: 'Endowment fund, patrons & corporate sponsors' },
  { id: 'logistics-security', name: '🛡️ Logistics & Protocol', desc: 'Palace grandstand, security & hospitality' },
];

export const MessagingHub: React.FC = () => {
  const [activeChannelId, setActiveChannelId] = useState('general-announcements');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [senderName, setSenderName] = useState('High Chief Dr. Aliu Momoh');
  const [senderVillage, setSenderVillage] = useState('Jattu-Uzairue');
  const [attachedDocTitle, setAttachedDocTitle] = useState('');
  const [dispatchEmailNotice, setDispatchEmailNotice] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const loadMessages = () => {
    setMessages(getMessages());
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const activeChannel = CHANNELS.find((c) => c.id === activeChannelId) || CHANNELS[0];
  const channelMessages = messages.filter((m) => m.channelId === activeChannelId);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageInput.trim()) return;

    const newMsg = addMessage({
      channelId: activeChannelId,
      senderId: `usr-${Date.now()}`,
      senderName,
      senderRole: 'Festival Coordinator',
      senderVillage,
      content: messageInput.trim(),
      attachedDocTitle: attachedDocTitle.trim() || undefined,
    });

    setMessageInput('');
    setAttachedDocTitle('');
    loadMessages();

    // Optional Resend Email Dispatch
    if (dispatchEmailNotice) {
      setIsSendingEmail(true);
      try {
        await fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipientEmail: 'info@erueuzairue.org', // or broadcast email
            recipientName: 'Uzairue Global Secretariat',
            subject: `[Uzairue Channel Dispatch] ${activeChannel.name}`,
            docTitle: attachedDocTitle || activeChannel.name,
            docType: 'Festival Broadcast',
            message: messageInput,
          }),
        });
      } catch (err) {
        console.error('Email dispatch failed:', err);
      } finally {
        setIsSendingEmail(false);
      }
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl grid grid-cols-1 md:grid-cols-4 min-h-[600px] text-white">
      {/* Channels Sidebar */}
      <div className="bg-slate-950 border-r border-slate-800 p-4 space-y-4 md:col-span-1">
        <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
          <span className="text-xl">💬</span>
          <div>
            <h3 className="font-serif text-amber-300 font-bold text-xs uppercase tracking-wider">
              Uzairue Channels
            </h3>
            <p className="text-[10px] text-slate-400">Festival Collaboration & News</p>
          </div>
        </div>

        <div className="space-y-1">
          {CHANNELS.map((ch) => {
            const isActive = ch.id === activeChannelId;
            const count = messages.filter((m) => m.channelId === ch.id).length;
            return (
              <button
                key={ch.id}
                onClick={() => setActiveChannelId(ch.id)}
                className={`w-full text-left p-2.5 rounded-xl transition-all cursor-pointer flex items-center justify-between gap-2 ${
                  isActive
                    ? 'bg-amber-500 text-slate-950 font-black shadow'
                    : 'text-slate-300 hover:bg-slate-900 hover:text-white'
                }`}
              >
                <div className="truncate text-xs font-bold">{ch.name}</div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono font-extrabold ${
                    isActive ? 'bg-slate-950 text-amber-300' : 'bg-slate-800 text-slate-400'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Sender Identity Preset */}
        <div className="bg-slate-900 border border-slate-800 p-3 rounded-xl space-y-2 mt-auto">
          <span className="text-[10px] font-bold text-amber-300 uppercase tracking-widest block">
            Posting Identity:
          </span>
          <input
            type="text"
            value={senderName}
            onChange={(e) => setSenderName(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-xs text-white"
            placeholder="Your Name / Title"
          />
          <input
            type="text"
            value={senderVillage}
            onChange={(e) => setSenderVillage(e.target.value)}
            className="w-full bg-slate-950 border border-slate-700 rounded p-1.5 text-xs text-amber-300"
            placeholder="Village / Clan"
          />
        </div>
      </div>

      {/* Main Chat Feed Area */}
      <div className="md:col-span-3 flex flex-col bg-slate-900/60">
        {/* Channel Header */}
        <div className="bg-slate-950 border-b border-slate-800 p-4 flex justify-between items-center">
          <div>
            <h3 className="font-serif text-amber-300 font-bold text-sm flex items-center gap-2">
              <span>{activeChannel.name}</span>
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">{activeChannel.desc}</p>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-950 px-2.5 py-1 rounded-full border border-emerald-800">
            🟢 {channelMessages.length} Messages Persisted
          </span>
        </div>

        {/* Messages Stream */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[420px]">
          {channelMessages.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-xs italic">
              No messages posted in this channel yet. Be the first to start the collaboration discussion!
            </div>
          ) : (
            channelMessages.map((msg) => (
              <div
                key={msg.id}
                className="bg-slate-950/80 border border-slate-800/80 p-3.5 rounded-xl space-y-2 max-w-2xl shadow-sm"
              >
                <div className="flex items-center justify-between gap-2 border-b border-slate-900 pb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-extrabold text-amber-300 text-xs">{msg.senderName}</span>
                    <span className="bg-emerald-950 text-emerald-300 text-[9px] px-1.5 py-0.5 rounded font-mono">
                      {msg.senderVillage}
                    </span>
                    <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">
                      {msg.senderRole}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500 font-mono">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                <p className="text-xs text-slate-200 leading-relaxed whitespace-pre-wrap">{msg.content}</p>

                {msg.attachedDocTitle && (
                  <div className="bg-emerald-950/80 border border-emerald-600/50 p-2 rounded-lg text-xs flex items-center justify-between gap-2 text-emerald-200">
                    <div className="flex items-center gap-1.5">
                      <span>📜</span>
                      <strong className="text-amber-300">{msg.attachedDocTitle}</strong>
                    </div>
                    <span className="text-[9px] uppercase font-mono bg-emerald-900 px-1.5 py-0.5 rounded">
                      Attached Document
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Message Input Form */}
        <form onSubmit={handleSendMessage} className="bg-slate-950 border-t border-slate-800 p-4 space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              placeholder={`Post message to ${activeChannel.name}...`}
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-400"
            />
            <button
              type="submit"
              disabled={!messageInput.trim()}
              className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-slate-950 font-extrabold px-5 py-2.5 rounded-xl shadow-md cursor-pointer text-xs flex items-center gap-1 shrink-0"
            >
              <span>🚀</span> Post
            </button>
          </div>

          <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
            {/* Optional Document Attachment */}
            <div className="flex items-center gap-2 flex-1">
              <span>📜 Link Document:</span>
              <input
                type="text"
                placeholder="e.g. Festival Schedule / Proclamation No. 4"
                value={attachedDocTitle}
                onChange={(e) => setAttachedDocTitle(e.target.value)}
                className="bg-slate-900 border border-slate-800 rounded px-2 py-1 text-[11px] text-amber-300 w-64"
              />
            </div>

            {/* Email Dispatch Checkbox */}
            <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-emerald-300 hover:text-emerald-200">
              <input
                type="checkbox"
                checked={dispatchEmailNotice}
                onChange={(e) => setDispatchEmailNotice(e.target.checked)}
                className="accent-emerald-500 rounded cursor-pointer"
              />
              <span>{isSendingEmail ? 'Dispatching Email...' : '✉️ Dispatch Email Notice'}</span>
            </label>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessagingHub;
